import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentConfigSnapshot,
  AgentConfigVersion,
  AgentWorkspace,
  Localized,
} from '@servicesai/api-client';
import {
  GlassButton,
  GlassCard,
  GlassInput,
  GlassSelect,
  GlassTextarea,
  StatusPill,
  useI18n,
} from '@servicesai/ui';
import { admin } from '../../lib/api.js';
import { S } from '../../strings.js';
import {
  MysteryShopperTargetEditor,
  mysteryShopperEditorIssues,
} from './MysteryShopperTargetEditor.js';
import { scrubModelIds } from '../../lib/aiBranding.js';

interface AgentConfigPanelProps {
  slug: string;
  workspace: AgentWorkspace;
}

type ConfigAction =
  | { kind: 'create'; sourceVersionId?: string }
  | { kind: 'save'; versionId: string; config: AgentConfigSnapshot; lockVersion: number; changeNote: string }
  | { kind: 'delete'; versionId: string }
  | { kind: 'submit'; versionId: string }
  | { kind: 'rollback'; versionId: string };

function cloneConfig(config: AgentConfigSnapshot): AgentConfigSnapshot {
  return structuredClone(config);
}

/** Model identifiers are sensitive vendor details: masked by default (like a
 *  secret), revealed only on demand by the administrator editing them. */
function MaskedModelInput({
  value,
  onChange,
  revealLabel,
  hideLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  revealLabel: string;
  hideLabel: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-1 flex items-center gap-2">
      <GlassInput
        type={revealed ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        dir="ltr"
        autoComplete="off"
        className="flex-1"
      />
      <GlassButton type="button" size="sm" variant="ghost" onClick={() => setRevealed((previous) => !previous)}>
        {revealed ? hideLabel : revealLabel}
      </GlassButton>
    </div>
  );
}

function ToggleField({
  checked,
  label,
  hint,
  onChange,
}: {
  checked: boolean;
  label: string;
  hint: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex min-h-16 cursor-pointer items-start gap-3 rounded-lg border border-aeblack-100 bg-white p-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0"
      />
      <span>
        <span className="block text-sm font-bold">{label}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-ink-faint">{hint}</span>
      </span>
    </label>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block text-xs font-bold text-ink-soft">
      {label}
      <GlassInput
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-1"
      />
    </label>
  );
}

export function AgentConfigPanel({ slug, workspace }: AgentConfigPanelProps) {
  const { l, lang } = useI18n();
  const queryClient = useQueryClient();
  const draft = workspace.versions.find((version) => version.state === 'draft') ?? null;
  const [config, setConfig] = useState<AgentConfigSnapshot>(() => cloneConfig(draft?.config ?? workspace.effectiveConfig));
  const [dirty, setDirty] = useState(false);
  const [changeNote, setChangeNote] = useState(draft?.change_note ?? '');
  const [issues, setIssues] = useState<{ path: string; message: string }[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [diffTarget, setDiffTarget] = useState<string>('');
  const [simulation, setSimulation] = useState<{
    promptPreview: string;
    schedulePreviews: { id?: string; name: string; occurrences: string[] }[];
    integrationEffects: {
      adapterKey: string;
      enabled: boolean;
      capabilities: string[];
      secretRefPresent: boolean;
    }[];
    authorityBoundary: string;
  } | null>(null);

  useEffect(() => {
    setConfig(cloneConfig(draft?.config ?? workspace.effectiveConfig));
    setChangeNote(draft?.change_note ?? '');
    setDirty(false);
    setIssues(null);
    setSimulation(null);
  }, [draft?.id, workspace.effectiveConfig]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] }),
      queryClient.invalidateQueries({ queryKey: ['agent-detail', slug] }),
      queryClient.invalidateQueries({ queryKey: ['agent-portfolio'] }),
    ]);
  };

  const actionMutation = useMutation({
    mutationFn: async (action: ConfigAction) => {
      if (action.kind === 'create') return admin().createAgentDraft(slug, action.sourceVersionId);
      if (action.kind === 'save') {
        return admin().updateAgentDraft(slug, action.versionId, {
          config: action.config,
          expectedLockVersion: action.lockVersion,
          changeNote: action.changeNote,
        });
      }
      if (action.kind === 'delete') return admin().deleteAgentDraft(slug, action.versionId);
      if (action.kind === 'submit') return admin().submitAgentDraft(slug, action.versionId);
      return admin().rollbackAgentConfig(slug, action.versionId);
    },
    onSuccess: async (_result, action) => {
      setDirty(false);
      setIssues(null);
      setNotice(
        action.kind === 'create'
          ? l(S.agentDraftCreated)
          : action.kind === 'save'
            ? l(S.draftSaved)
            : action.kind === 'delete'
              ? l(S.agentDraftDiscarded)
              : action.kind === 'submit'
                ? l(S.agentSubmitted)
                : l(S.agentRollbackCreated),
      );
      await refresh();
    },
  });

  const validateMutation = useMutation({
    mutationFn: () => admin().validateAgentConfig(slug, config),
    onSuccess: (result) => {
      setIssues(result.issues);
      setNotice(result.valid ? l(S.agentConfigValid) : null);
    },
  });
  const simulateMutation = useMutation({
    mutationFn: () => admin().simulateAgentConfig(slug, config, lang),
    onSuccess: (result) => {
      setSimulation(result);
      setIssues([]);
    },
  });

  const fromVersionId = workspace.publishedVersion?.id ?? '';
  const diffQuery = useQuery({
    queryKey: ['agent-config-diff', slug, fromVersionId, diffTarget],
    queryFn: () => admin().agentConfigDiff(slug, fromVersionId, diffTarget),
    enabled: Boolean(fromVersionId && diffTarget && fromVersionId !== diffTarget),
  });

  const replace = (next: AgentConfigSnapshot) => {
    setConfig(next);
    setDirty(true);
    setIssues(null);
    setNotice(null);
  };
  const updateGuidance = (locale: keyof Localized, value: string) =>
    replace({
      ...config,
      prompt: {
        ...config.prompt,
        guidance: { ...config.prompt.guidance, [locale]: value },
      },
    });
  const updateInstruction = (index: number, locale: keyof Localized, value: string) => {
    const systemInstructions = config.prompt.systemInstructions.map((instruction, itemIndex) =>
      itemIndex === index ? { ...instruction, [locale]: value } : instruction,
    );
    replace({ ...config, prompt: { ...config.prompt, systemInstructions } });
  };
  const updateRuntime = <K extends keyof AgentConfigSnapshot['runtime']>(
    key: K,
    value: AgentConfigSnapshot['runtime'][K],
  ) => replace({ ...config, runtime: { ...config.runtime, [key]: value } });
  const updateGuardrail = <K extends keyof AgentConfigSnapshot['guardrails']>(
    key: K,
    value: AgentConfigSnapshot['guardrails'][K],
  ) => replace({ ...config, guardrails: { ...config.guardrails, [key]: value } });

  const actionError = actionMutation.error ?? validateMutation.error ?? simulateMutation.error ?? diffQuery.error;
  const busy = actionMutation.isPending || validateMutation.isPending || simulateMutation.isPending;
  const editable = Boolean(draft);
  const editorIssues = slug === 'mystery-shopper' ? mysteryShopperEditorIssues(config) : [];

  return (
    <div className="space-y-4">
      <GlassCard className="sticky top-0 z-20 p-5 shadow-sm" strong>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-extrabold">{l(S.promptGuidance)}</h2>
              <StatusPill
                state={draft ? 'draft' : 'published'}
                label={draft ? `${l(S.draft)} v${draft.version}` : `${l(S.published)} v${workspace.publishedVersion?.version ?? '—'}`}
              />
              {dirty && <StatusPill state="in_progress" label={l(S.unsavedChanges)} />}
            </div>
            <p className="mt-1 max-w-3xl text-xs leading-relaxed text-ink-faint">
              {l(S.promptGuidanceHint)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!draft ? (
              <GlassButton
                variant="primary"
                disabled={busy}
                onClick={() => actionMutation.mutate({ kind: 'create', sourceVersionId: workspace.publishedVersion?.id })}
              >
                {l(S.createDraft)}
              </GlassButton>
            ) : (
              <>
                <GlassButton
                  disabled={busy}
                  onClick={() => validateMutation.mutate()}
                >
                  {validateMutation.isPending ? l(S.validating) : l(S.validate)}
                </GlassButton>
                <GlassButton
                  disabled={busy}
                  onClick={() => simulateMutation.mutate()}
                >
                  {simulateMutation.isPending ? l(S.validating) : l(S.simulateConfiguration)}
                </GlassButton>
                <GlassButton
                  variant="primary"
                  disabled={busy || !dirty || editorIssues.length > 0}
                  title={editorIssues.length > 0
                    ? (lang === 'ar'
                        ? 'أكمل متطلبات إعداد المتسوق الخفي قبل الحفظ.'
                        : 'Complete the Mystery Shopper setup checks before saving.')
                    : undefined}
                  onClick={() => actionMutation.mutate({
                    kind: 'save',
                    versionId: draft.id,
                    config,
                    lockVersion: draft.lock_version,
                    changeNote,
                  })}
                >
                  {actionMutation.isPending && actionMutation.variables?.kind === 'save' ? l(S.saving) : l(S.saveDraft)}
                </GlassButton>
                <GlassButton
                  disabled={busy || dirty}
                  title={dirty ? l(S.saveBeforeSubmit) : undefined}
                  onClick={() => actionMutation.mutate({ kind: 'submit', versionId: draft.id })}
                >
                  {l(S.submitForApproval)}
                </GlassButton>
                <GlassButton
                  variant="danger"
                  disabled={busy}
                  onClick={() => actionMutation.mutate({ kind: 'delete', versionId: draft.id })}
                >
                  {l(S.discardDraft)}
                </GlassButton>
              </>
            )}
          </div>
        </div>
      </GlassCard>

      {notice && (
        <div className="aegov-alert alert-success" role="status">
          <div className="alert-content">{notice}</div>
        </div>
      )}
      {actionError && (
        <div className="aegov-alert alert-error" role="alert">
          <div className="alert-content">{l(S.actionFailed)}: {(actionError as Error).message}</div>
        </div>
      )}
      {issues && issues.length > 0 && (
        <div className="aegov-alert alert-error" role="alert">
          <div className="alert-content">
            <div className="font-bold">{issues.length} {l(S.issues)}</div>
            {issues.map((issue) => (
              <div key={`${issue.path}-${issue.message}`} className="text-xs">
                <code>{issue.path || 'config'}</code>: {issue.message}
              </div>
            ))}
          </div>
        </div>
      )}
      {simulation && (
        <GlassCard className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-extrabold">{l(S.simulationPreview)}</h3>
              <p className="mt-1 text-xs text-ink-faint">{simulation.authorityBoundary}</p>
            </div>
            <StatusPill state="published" label={l(S.validated)} />
          </div>
          <details className="mt-4 rounded-lg border border-aeblack-100 p-3">
            <summary className="cursor-pointer text-xs font-bold">{l(S.promptPreview)}</summary>
            <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-aeblack-50 p-3 text-[10px]" dir="auto">
              {simulation.promptPreview}
            </pre>
          </details>
          <div className="mt-3 grid gap-2 lg:grid-cols-2">
            {simulation.schedulePreviews.map((schedule) => (
              <div key={schedule.id ?? schedule.name} className="rounded-lg border border-aeblack-100 p-3">
                <div className="text-xs font-bold">{schedule.name}</div>
                <div className="mt-1 space-y-1 font-mono text-[10px] text-ink-faint">
                  {schedule.occurrences.map((occurrence) => <div key={occurrence}>{occurrence}</div>)}
                </div>
              </div>
            ))}
            {simulation.integrationEffects.map((binding) => (
              <div key={binding.adapterKey} className="rounded-lg border border-aeblack-100 p-3 text-xs">
                <div className="font-bold">{binding.adapterKey}</div>
                <div className="mt-1 text-ink-faint">{binding.capabilities.join(', ') || '—'}</div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      <fieldset disabled={!editable || busy} className="space-y-4 disabled:opacity-70">
        {slug === 'mystery-shopper' && (
          <MysteryShopperTargetEditor config={config} onChange={replace} />
        )}

        <GlassCard className="p-5">
          <legend className="sr-only">{l(S.bilingualGuidance)}</legend>
          <div className="mb-3">
            <h3 className="text-base font-extrabold">{l(S.bilingualGuidance)}</h3>
            <p className="mt-1 text-xs text-ink-faint">{l(S.bilingualGuidanceHint)}</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <label className="block text-xs font-bold text-ink-soft">
              {l(S.guidanceEnglish)}
              <GlassTextarea
                value={config.prompt.guidance.en}
                onChange={(event) => updateGuidance('en', event.target.value)}
                rows={8}
                dir="ltr"
                className="mt-1"
              />
            </label>
            <label className="block text-xs font-bold text-ink-soft">
              {l(S.guidanceArabic)}
              <GlassTextarea
                value={config.prompt.guidance.ar}
                onChange={(event) => updateGuidance('ar', event.target.value)}
                rows={8}
                dir="rtl"
                className="mt-1"
              />
            </label>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-extrabold">{l(S.systemInstructions)}</h3>
              <p className="mt-1 text-xs text-ink-faint">{l(S.systemInstructionsHint)}</p>
            </div>
            <GlassButton
              size="sm"
              type="button"
              onClick={() => replace({
                ...config,
                prompt: {
                  ...config.prompt,
                  systemInstructions: [...config.prompt.systemInstructions, { en: '', ar: '' }],
                },
              })}
            >
              {l(S.addInstruction)}
            </GlassButton>
          </div>
          <div className="mt-4 space-y-3">
            {config.prompt.systemInstructions.map((instruction, index) => (
              <div key={index} className="grid gap-2 rounded-lg border border-aeblack-100 bg-aeblack-50 p-3 lg:grid-cols-[1fr_1fr_auto]">
                <label className="text-[11px] font-bold text-ink-faint">
                  {l(S.english)}
                  <GlassTextarea
                    value={instruction.en}
                    onChange={(event) => updateInstruction(index, 'en', event.target.value)}
                    rows={3}
                    dir="ltr"
                    className="mt-1"
                  />
                </label>
                <label className="text-[11px] font-bold text-ink-faint">
                  {l(S.arabic)}
                  <GlassTextarea
                    value={instruction.ar}
                    onChange={(event) => updateInstruction(index, 'ar', event.target.value)}
                    rows={3}
                    dir="rtl"
                    className="mt-1"
                  />
                </label>
                <GlassButton
                  size="sm"
                  variant="danger"
                  type="button"
                  className="self-end"
                  aria-label={`${l(S.removeInstruction)} ${index + 1}`}
                  onClick={() => replace({
                    ...config,
                    prompt: {
                      ...config.prompt,
                      systemInstructions: config.prompt.systemInstructions.filter((_, itemIndex) => itemIndex !== index),
                    },
                  })}
                >
                  {l(S.remove)}
                </GlassButton>
              </div>
            ))}
          </div>
        </GlassCard>

        <div className="grid gap-4 xl:grid-cols-2">
          <GlassCard className="p-5">
            <h3 className="text-base font-extrabold">{l(S.runtimeControls)}</h3>
            <p className="mt-1 text-xs text-ink-faint">{l(S.runtimeControlsHint)}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block text-xs font-bold text-ink-soft sm:col-span-2">
                {l(S.model)}
                <MaskedModelInput
                  value={config.runtime.model}
                  onChange={(next) => updateRuntime('model', next)}
                  revealLabel={l({ en: 'Reveal', ar: 'إظهار' })}
                  hideLabel={l({ en: 'Hide', ar: 'إخفاء' })}
                />
              </label>
              <NumberField label={l(S.temperature)} value={config.runtime.temperature} min={0} max={2} step={0.1} onChange={(value) => updateRuntime('temperature', value)} />
              <NumberField label={l(S.maxTokens)} value={config.runtime.maxTokens} min={100} max={16000} onChange={(value) => updateRuntime('maxTokens', value)} />
              <NumberField label={l(S.timeoutMs)} value={config.runtime.timeoutMs} min={1000} max={300000} step={1000} onChange={(value) => updateRuntime('timeoutMs', value)} />
              <label className="block text-xs font-bold text-ink-soft">
                {l(S.responseTone)}
                <GlassSelect
                  value={config.prompt.responseStyle.tone}
                  onChange={(event) => replace({
                    ...config,
                    prompt: {
                      ...config.prompt,
                      responseStyle: {
                        ...config.prompt.responseStyle,
                        tone: event.target.value as AgentConfigSnapshot['prompt']['responseStyle']['tone'],
                      },
                    },
                  })}
                  className="mt-1"
                >
                  {['formal', 'professional', 'warm', 'concise', 'detailed'].map((tone) => (
                    <option key={tone} value={tone}>{tone}</option>
                  ))}
                </GlassSelect>
              </label>
              <NumberField label={l(S.minSentences)} value={config.prompt.responseStyle.minSentences} min={1} max={20} onChange={(value) => replace({ ...config, prompt: { ...config.prompt, responseStyle: { ...config.prompt.responseStyle, minSentences: value } } })} />
              <NumberField label={l(S.maxSentences)} value={config.prompt.responseStyle.maxSentences} min={1} max={50} onChange={(value) => replace({ ...config, prompt: { ...config.prompt, responseStyle: { ...config.prompt.responseStyle, maxSentences: value } } })} />
            </div>
          </GlassCard>

          <GlassCard className="p-5">
            <h3 className="text-base font-extrabold">{l(S.guardrails)}</h3>
            <p className="mt-1 text-xs text-ink-faint">{l(S.guardrailsHint)}</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <ToggleField checked={config.guardrails.requireGrounding} label={l(S.requireGrounding)} hint={l(S.requireGroundingHint)} onChange={(value) => updateGuardrail('requireGrounding', value)} />
              <ToggleField checked={config.guardrails.allowExternalLookup} label={l(S.externalLookup)} hint={l(S.externalLookupHint)} onChange={(value) => updateGuardrail('allowExternalLookup', value)} />
              <ToggleField checked={config.guardrails.redactPersonalData} label={l(S.redactPersonalData)} hint={l(S.redactPersonalDataHint)} onChange={(value) => updateGuardrail('redactPersonalData', value)} />
              <ToggleField checked={config.guardrails.humanApprovalForActions} label={l(S.humanApproval)} hint={l(S.humanApprovalHint)} onChange={(value) => updateGuardrail('humanApprovalForActions', value)} />
            </div>
            <label className="mt-3 block text-xs font-bold text-ink-soft">
              {l(S.blockedTopics)}
              <GlassTextarea
                value={config.guardrails.blockedTopics.join('\n')}
                onChange={(event) => updateGuardrail(
                  'blockedTopics',
                  event.target.value.split('\n').map((value) => value.trim()).filter(Boolean),
                )}
                rows={4}
                className="mt-1"
                placeholder={l(S.onePerLine)}
              />
            </label>
          </GlassCard>
        </div>

        <GlassCard className="p-5">
          <label className="block text-xs font-bold text-ink-soft">
            {l(S.changeNote)}
            <GlassInput
              value={changeNote}
              onChange={(event) => {
                setChangeNote(event.target.value);
                setDirty(true);
              }}
              className="mt-1"
              placeholder={l(S.changeNoteHint)}
            />
          </label>
        </GlassCard>
      </fieldset>

      <GlassCard className="p-5">
        <div>
          <h3 className="text-base font-extrabold">{l(S.versionHistory)}</h3>
          <p className="mt-1 text-xs text-ink-faint">{l(S.versionHistoryHint)}</p>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-aeblack-50 text-[10px] font-bold text-ink-faint uppercase">
              <tr>
                <th className="p-3 text-start">{l(S.version)}</th>
                <th className="p-3 text-start">{l(S.status)}</th>
                <th className="p-3 text-start">{l(S.changeNote)}</th>
                <th className="p-3 text-start">{l(S.updated)}</th>
                <th className="p-3 text-end">{l(S.actions)}</th>
              </tr>
            </thead>
            <tbody>
              {workspace.versions.map((version) => (
                <VersionRow
                  key={version.id}
                  version={version}
                  lang={lang}
                  isPublished={workspace.publishedVersion?.id === version.id}
                  draftExists={Boolean(draft)}
                  busy={busy}
                  onDiff={() => setDiffTarget(version.id)}
                  onRollback={() => actionMutation.mutate({ kind: 'rollback', versionId: version.id })}
                />
              ))}
            </tbody>
          </table>
        </div>
        {diffTarget && fromVersionId === diffTarget && (
          <p className="mt-3 text-xs text-ink-faint">{l(S.selectedPublishedVersion)}</p>
        )}
        {diffQuery.isLoading && <p className="mt-3 text-xs text-ink-faint">{l(S.loadingDiff)}</p>}
        {diffQuery.data && (
          <div className="mt-4 overflow-hidden rounded-lg border border-aeblack-100">
            <div className="flex items-center justify-between bg-aeblack-50 px-3 py-2 text-xs">
              <strong>{l(S.configurationDiff)}</strong>
              <span>{diffQuery.data.changes.length} {l(S.changes)}</span>
            </div>
            {diffQuery.data.changes.length === 0 ? (
              <p className="p-3 text-xs text-ink-faint">{l(S.noConfigurationChanges)}</p>
            ) : (
              <div className="max-h-96 overflow-auto">
                {diffQuery.data.changes.map((change, index) => (
                  <div key={`${change.section}-${index}`} className="border-t border-aeblack-100 p-3">
                    <code className="text-xs font-bold">{change.section}</code>
                    <div className="mt-2 grid gap-2 lg:grid-cols-2" dir="ltr">
                      <pre className="overflow-auto whitespace-pre-wrap rounded bg-aered-50 p-2 text-[10px]">{change.before === undefined ? '—' : scrubModelIds(JSON.stringify(change.before, null, 2))}</pre>
                      <pre className="overflow-auto whitespace-pre-wrap rounded bg-aegreen-50 p-2 text-[10px]">{change.after === undefined ? '—' : scrubModelIds(JSON.stringify(change.after, null, 2))}</pre>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function VersionRow({
  version,
  lang,
  isPublished,
  draftExists,
  busy,
  onDiff,
  onRollback,
}: {
  version: AgentConfigVersion;
  lang: 'en' | 'ar';
  isPublished: boolean;
  draftExists: boolean;
  busy: boolean;
  onDiff: () => void;
  onRollback: () => void;
}) {
  const { l } = useI18n();
  return (
    <tr className="border-t border-aeblack-100">
      <td className="p-3 font-mono font-bold">v{version.version}</td>
      <td className="p-3">
        <StatusPill
          state={version.state === 'published' ? 'published' : version.state === 'rejected' ? 'rejected' : version.state === 'pending_approval' ? 'in_review' : version.state}
          label={version.state.replaceAll('_', ' ')}
        />
      </td>
      <td className="max-w-64 p-3 text-xs text-ink-soft">{version.change_note || '—'}</td>
      <td className="p-3 text-xs text-ink-faint">{new Date(version.updated_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</td>
      <td className="p-3">
        <div className="flex justify-end gap-2">
          {!isPublished && <GlassButton size="sm" onClick={onDiff}>{l(S.compare)}</GlassButton>}
          {version.state !== 'draft' && (
            <GlassButton
              size="sm"
              disabled={busy || draftExists}
              title={draftExists ? l(S.discardDraftBeforeRollback) : undefined}
              onClick={onRollback}
            >
              {l(S.rollback)}
            </GlassButton>
          )}
        </div>
      </td>
    </tr>
  );
}
